import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '@/lib/api'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { OrgSummary } from '@/types/onboarding'

/**
 * Every organisation the signed-in user can act in — owned first, then the
 * ones they were invited to.
 *
 * Backed by the same predicate AuthMiddleware uses to resolve
 * `request.state.org_id` (`dashboard/middleware/org_context.py`
 * `MEMBER_ORGS_SQL`), so the first entry is the org a request lands in when
 * nothing is selected. If the two ever drift the switcher opens showing one
 * org while every request is scoped to another, with nothing on screen saying
 * so.
 *
 * `staleTime` is generous: org membership changes at human speed, and this is
 * mounted on every authenticated page.
 */
export function useOrgs() {
  return useQuery<{ organizations: OrgSummary[] }>({
    queryKey: ['organizations'],
    queryFn: () => apiFetch<{ organizations: OrgSummary[] }>('/api/organizations'),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Does the signed-in user OWN the org they are currently acting in?
 *
 * Plan 54 decision 7 gates the account-type switch on ownership specifically, not
 * on the owner+admin tier that governs the rest of billing. `authStore.isOrgAdmin`
 * is the wrong signal for it: `/api/auth/me` sets that only when the caller owns a
 * BUSINESS org, so it is false for the owner of an individual org — exactly the
 * person who needs the switch most.
 *
 * Derived from `useOrgs()` instead, which reports `is_owner` per org against the
 * acting org id. Defaults to false while the list is loading, so the control starts
 * disabled and enables once ownership is known rather than flickering the other way.
 */
export function useIsActingOrgOwner(): boolean {
  const activeOrgId = useWorkspaceStore((s) => s.activeOrgId)
  const { data } = useOrgs()
  if (!activeOrgId) return false
  return (data?.organizations ?? []).some((o) => o.id === activeOrgId && o.is_owner)
}
